import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useUpdateBeaconAllocation, BeaconAllocation } from "@/lib/api/fund";
import { AllocationForm } from "./AllocationForm";
import { safeParseDecimal } from "@/lib/api/common";
import { useQueryParam } from "@/hooks/use-query-param";
import { Skeleton } from "@/components/ui/skeleton";

interface UpdateAllocationModalProps {
  allocations: BeaconAllocation[];
}

export function UpdateAllocationModal({ allocations }: UpdateAllocationModalProps) {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allocationId, setAllocationId] = useQueryParam("allocation_id", "");
  const [isSaving, setIsSaving] = useState(false);
  
  const updateAllocation = useUpdateBeaconAllocation();
  
  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    amount_allocated: "",
    is_discretionary: false,
    customer_id: "",
    employee_id: "",
  });

  const isOpen = !!allocationId;
  const currentAllocation = allocations.find(a => a.id === allocationId);

  useEffect(() => {
    if (currentAllocation) {
      setFormData({
        year: currentAllocation.year,
        amount_allocated: currentAllocation.amount_allocated || "",
        is_discretionary: currentAllocation.is_discretionary || false,
        customer_id: currentAllocation.customer_id || "",
        employee_id: currentAllocation.employee_id || "",
      });
    }
  }, [currentAllocation]);

  const handleClose = () => {
    setAllocationId("");
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      year: new Date().getFullYear(),
      amount_allocated: "",
      is_discretionary: false,
      customer_id: "",
      employee_id: "",
    });
  };

  const handleSave = async () => {
    if (!formData.amount_allocated || (!formData.customer_id && !formData.employee_id)) {
      toast({
        title: "Error",
        description: "Please fill in all required fields and select either a customer or employee.",
        variant: "destructive",
      });
      return;
    }

    const parsedAmount = safeParseDecimal({ key: "amount_allocated" });
    const amount = parsedAmount(formData.amount_allocated);
    if (!amount || parseFloat(amount.toString()) < 0) {
      toast({
        title: "Error",
        description: "Please enter a valid amount.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    
    try {
      const result = await updateAllocation({
        id: allocationId,
        year: formData.year,
        amount_allocated: formData.amount_allocated,
        is_discretionary: formData.is_discretionary,
        customer_id: formData.customer_id || null,
        employee_id: formData.employee_id || null,
      });

      if (result.success) {
        toast({
          title: "Success",
          description: "Allocation updated successfully.",
        });
        handleClose();
      } else {
        toast({
          title: "Error",
          description: "Failed to update allocation.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isOpen && !currentAllocation) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Allocation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Update Allocation</DialogTitle>
          <DialogDescription>
            Modify the fund allocation details.
          </DialogDescription>
        </DialogHeader>
        
        <AllocationForm 
          formData={formData}
          setFormData={setFormData}
          isEditing={true}
        />
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}